// Straight from the modules, not the feature index: the index re-exports the
// whole Explorer screen, which a slide has no business loading.
import { ExplorerCell } from 'features/Explorer/components/ExplorerCell'
import { EMPTY_CELL, formatCell, isNumericType, rowCountLabel } from 'features/Explorer/utils/explorerFormat'
import { cn } from 'lib/utils'

import { EmptyBlock } from '../EmptyBlock'
import { densityFor, layoutPages, pageCount, readExplorerTable, type TableLine } from './explorerTable'

/**
 * An Explorer grid, sized for a slide (MYD-504).
 *
 * Cells go through Explorer's own ExplorerCell, so a currency, date or badge
 * column reads exactly as it does in Explorer; only the scale changes. The
 * footer says what the table is NOT showing (more rows, the filters, columns
 * past the cap), because a slide has no scrollbar to hint at it.
 *
 * Pure: the page comes from the payload, set by the presenter on a
 * continuation slide (usePresenter.presentationSlides).
 */
const DENSITY = {
	regular: { text: 'text-[17px]', cell: 'py-2', head: 'py-2 text-[14px]', group: 'py-1.5 text-[15px]', badge: '[&_div]:text-[14px]' },
	compact: { text: 'text-[15px]', cell: 'py-1', head: 'py-1.5 text-[13px]', group: 'py-1 text-[14px]', badge: '[&_div]:text-[13px]' },
	dense: { text: 'text-[13px] leading-tight', cell: 'py-[3px]', head: 'py-1 text-[12px]', group: 'py-[3px] text-[13px]', badge: '[&_div]:py-0 [&_div]:text-[11px]' },
}

export function ExplorerTableBlock({ payload }: { payload: any }) {
	const table = readExplorerTable(payload)
	if (!table) return <EmptyBlock>This table could not be shown.</EmptyBlock>
	if (table.empty) return <EmptyBlock>{table.empty}</EmptyBlock>
	if (table.total === 0 || table.rows.length === 0) return <EmptyBlock>No {table.rowLabel}s match this table.</EmptyBlock>

	const pages = layoutPages(table)
	const count = pageCount(table)
	const page = Math.min(table.page ?? 0, count - 1)
	const lines = pages[page] ?? []
	const dataRows = lines.filter((line): line is Extract<TableLine, { kind: 'row' }> => line.kind === 'row')
	const first = (dataRows[0]?.index ?? 0) + 1
	const last = (dataRows[dataRows.length - 1]?.index ?? 0) + 1
	const remaining = count - 1 - page
	const density = DENSITY[densityFor(table.rowLimit)]

	const groupColumn = table.columns.find(column => column.key === table.groupBy?.key)
	const groupLabel = (value: unknown) => {
		const text = formatCell(value, groupColumn?.type ?? 'text')
		return text === EMPTY_CELL ? `No ${table.groupBy?.label ?? 'value'}` : text
	}

	return (
		<div className={density.text}>
			<table className="w-full table-fixed border-collapse text-left">
				<thead>
					<tr className="border-b-2 border-gray-200">
						{table.columns.map(column => (
							<th
								key={column.key}
								scope="col"
								className={cn('truncate px-3 font-semibold uppercase tracking-wide text-gray-500', density.head, isNumericType(column.type) && 'text-right')}
							>
								{column.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{lines.map((line, index) =>
						line.kind === 'group' ? (
							<tr key={`group-${index}`} className="bg-gray-50">
								<th colSpan={table.columns.length} scope="colgroup" className={cn('px-3 font-semibold text-gray-700', density.group)}>
									{groupLabel(line.value)}
								</th>
							</tr>
						) : (
							<tr key={line.row.id ?? index} className="border-b border-gray-100">
								{table.columns.map(column => (
									// The badge is Explorer's, sized for a grid; the descendant rule scales it for a slide.
									<td key={column.key} className={cn('truncate px-3 text-gray-800', density.cell, density.badge)}>
										<ExplorerCell value={line.row[column.key]} column={column} />
									</td>
								))}
							</tr>
						),
					)}
				</tbody>
			</table>

			<p className="mt-3 text-[14px] text-gray-500" data-testid="explorer-table-footer">
				{[
					`Showing ${first === last ? first : `${first}–${last}`} of ${rowCountLabel(table.total, table.rowLabel)}`,
					table.filters.length > 0 && `Filters: ${table.filters.join('; ')}`,
					table.hiddenColumns > 0 && `${table.hiddenColumns} more ${table.hiddenColumns === 1 ? 'column' : 'columns'} in Explorer`,
					remaining > 0 && `Continues on ${remaining} more ${remaining === 1 ? 'slide' : 'slides'}`,
				]
					.filter(Boolean)
					.join(' · ')}
			</p>
		</div>
	)
}

export default ExplorerTableBlock
